import React, { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Compass, Lock, Menu, X } from "lucide-react";
import { useLang } from "@/lib/i18n";

const LANGS = ["it", "en", "es"];

export default function Nav() {
  const { t, lang, setLang } = useLang();
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => setOpen(false), [location.pathname]);

  const links = [
    { to: "/", key: "nav.home", id: "nav-link-home" },
    { to: "/viaggi", key: "nav.expeditions", id: "nav-link-expeditions" },
    { to: "/leader", key: "nav.leaders", id: "nav-link-leaders" },
    { to: "/chi-siamo", key: "nav.about", id: "nav-link-about" },
    { to: "/contatti", key: "nav.contact", id: "nav-link-contact" },
  ];

  return (
    <>
      <header
        className={"fixed top-0 left-0 right-0 z-50 transition-all duration-500 " + (scrolled ? "backdrop-blur-md bg-obsidian/80 border-b border-copper/20" : "bg-transparent")}
        data-testid="site-header"
      >
        <div className="container-x flex items-center justify-between h-16 sm:h-20">
          <Link to="/" className="flex items-center gap-2.5 group" data-testid="nav-logo">
            <Compass className="h-6 w-6 text-copper transition-transform duration-700 group-hover:rotate-180" />
            <span className="font-serif text-lg sm:text-xl tracking-[0.18em] uppercase">De Los Cielos</span>
          </Link>

          <nav className="hidden lg:flex items-center gap-8">
            {links.map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                data-testid={l.id}
                className={({ isActive }) => "link-underline font-mono text-[11px] uppercase tracking-[0.22em] transition-colors " + (isActive ? "text-copper" : "text-cream2 hover:text-cream")}
              >
                {t(l.key)}
              </NavLink>
            ))}
          </nav>

          <div className="flex items-center gap-4">
            <div className="hidden sm:flex items-center gap-1 font-mono text-[11px]" data-testid="language-selector">
              {LANGS.map((l, i) => (
                <span key={l} className="flex items-center">
                  {i > 0 && <span className="text-cream/20 px-0.5">/</span>}
                  <button
                    onClick={() => setLang(l)}
                    data-testid={`lang-btn-${l}`}
                    className={"px-1.5 py-1 uppercase tracking-widest transition-colors " + (lang === l ? "text-copper" : "text-sand hover:text-cream")}
                  >
                    {l}
                  </button>
                </span>
              ))}
            </div>
            <button className="lg:hidden text-cream" onClick={() => setOpen(true)} data-testid="nav-menu-open" aria-label="Menu">
              <Menu className="h-6 w-6" />
            </button>
          </div>
        </div>
      </header>

      <AnimatePresence>
        {open && (
          <motion.div
            className="fixed inset-0 z-[60] bg-obsidian/97 backdrop-blur-lg flex flex-col"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            data-testid="mobile-menu"
          >
            <div className="container-x flex items-center justify-between h-16">
              <span className="font-serif text-lg tracking-[0.18em] uppercase">De Los Cielos</span>
              <button onClick={() => setOpen(false)} data-testid="nav-menu-close" aria-label="Chiudi" className="text-cream">
                <X className="h-7 w-7" />
              </button>
            </div>
            <nav className="container-x flex flex-col gap-2 mt-10">
              {links.map((l, i) => (
                <motion.div key={l.to} initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.08 * i, duration: 0.6 }}>
                  <Link to={l.to} data-testid={l.id} className="font-serif text-4xl sm:text-5xl text-cream hover:text-copper transition-colors">
                    {t(l.key)}
                  </Link>
                </motion.div>
              ))}
              <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.35, duration: 0.6 }} className="mt-8 flex flex-col gap-5">
                <div className="flex items-center gap-2 font-mono text-sm">
                  {LANGS.map((l) => (
                    <button key={l} onClick={() => setLang(l)} data-testid={`lang-btn-${l}`} className={"px-2 py-1 uppercase " + (lang === l ? "text-copper" : "text-sand")}>
                      {l}
                    </button>
                  ))}
                </div>
                <Link to="/admin/login" data-testid="admin-toggle-btn" className="inline-flex items-center gap-2 border border-copper/30 text-copperlight px-4 py-2 font-mono text-[11px] uppercase tracking-[0.2em] w-max">
                  <Lock className="h-3 w-3" /> {t("nav.area")}
                </Link>
              </motion.div>
            </nav>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
